<template>
    <div class="flex flex-wrap gap-2.5">
        <div
            v-for="option in swatchOptions"
            :key="option.value"
            v-auto-animate
        >
            <swatch-button
                class="border border-transparent hover:border-black duration-200 transition-colors"
                :input-name="optionName"
                :input-value="option.value"
                :input-color="option.color"
                :input-image="option.image"
                :selected="option.selected || option.groupSelected"
                :variant="variant"
                :debug-value="option.debugValue"
                :label="option.label"
                :no-thanks="option.placeholder"
                :price="option.price"
                :is-disabled="option.isDisabled"
                :disabled-reason="option.disabledReason"
                :disabled-reason-body="option.disabledReasonBody"
                @valueChange="updateGroupedSelectedValue(option, $event)"
            />

            <div
                v-if="option.label && !hideLabels"
                :class="[
                    textWidth[variantWithDefault] ? textWidth[variantWithDefault].wrapperClasses : 'w-full',
                    { 'opacity-40': option.isDisabled },
                ]"
                class="mt-2 mb-4 pr-1"
            >
                <p
                    v-if="!option.placeholder"
                    class="text-sm font-semibold font-oceanside"
                >
                    {{ option.label }}
                </p>

                <p
                    v-if="option.isDisabled && option.disabledReasonBody"
                    class="mt-4 text-sm leading-none"
                >
                    {{option.disabledReasonBody}}
                </p>

                <div v-if="option.learnMore">
                    <info-modal
                        :text="option.learnMore"
                    ></info-modal>
                </div>
            </div>

            <swatch-button-options
                v-if="groupBy && option.groupSelected"
                :group-by-label="groupByLabel"
                :selected-value="selectedValue"
                :option="option"
                @selected="updateSelectedValue(option, $event)"
            >
            </swatch-button-options>
        </div>
    </div>
</template>

<script>
    import SwatchButton from './SwatchButton.vue';
    import SwatchButtonOptions from './SwatchButtonOptions.vue';
    import InfoModal from '../InfoModal.vue';

    import allStores from '../../stores/all';

    import getImage from '../../helpers/getimage';

    export default {
        mixins: [allStores],

        components: {
            SwatchButton,
            SwatchButtonOptions,
            InfoModal,
        },

        props: {
            itemData: Object,
            optionName: {
                type: String,
                required: true,
            },
            variant: {
                type: String,
            },
            options: {
                type: Array,
                required: true,
            },
            groupBy: String,
            groupByLabel: String,
            groupByDefault: String,
            selectedValue: {
                type: String,
                required: false,
            },
            hideLabels: {
                type: Boolean,
                default: false,
            },
        },

        computed: {
            viewController() {
                return this.customizerStore.viewController;
            },

            currencyCode() {
                return this.customizerStore.options.currency ?? 'USD';
            },

            locale() {
                return this.customizerStore.options.locale ?? 'en-US';
            },

            handgroundSelected() {
                return this.customizerStore.handgroundSelected;
            },

            /**
             * Pre-format the list of options.
             */
            swatchOptions() {
                if (this.options == null) {
                    return [];
                }

                // Check for selection limits.
                let selectedInGroup = 0;
                let atLimit = false;

                if (this.itemData) {
                    // eslint-disable-next-line no-restricted-syntax
                    for (const placement of this.itemData.placements) {
                        const c = this.viewController.selectedAtPlacement(placement.code);

                        if (c?.placeholder !== true) {
                            selectedInGroup += 1;
                        }
                    }

                    if (this.itemData?.custom['v-limit']) {
                        if (selectedInGroup >= (+this.itemData.custom['v-limit'])) {
                            atLimit = true;
                        }
                    }
                }

                const swatches = this.options.filter((option) => {
                    if (option.placeholder) {
                        // Placeholders should not be shown to users since they (placeholders) should only ever be present
                        // in the initial state only.
                        //
                        // Unless they are "No Thanks" options.
                        if (!option.placeholderOptional) {
                            return false;
                        }
                    }

                    return true;
                }).map((option) => {
                    let hint = null;

                    if (option.custom?.['v-hint-title'] && option.custom?.['v-hint-copy']) {
                        hint = {
                            title: option.custom?.['v-hint-title'],
                            copy: option.custom?.['v-hint-copy'],
                        };
                    }

                    let order = null;

                    // Put No Thanks buttons at the end.
                    if (option.placeholder) {
                        order = 10000;
                    } else {
                        order = +option.custom['v-rank'];
                    }

                    // Only show individual prices when each option is priced.
                    const price = option.priceOnce == null ? this.getPrice(option) : null;

                    // When displaying buttons, prefer to use descriptions, since they would contain more information.
                    let label = this.getDescription(option);

                    if (price) {
                        if (price.startsWith('+')) {
                            label += ` ${price}`;
                        } else {
                            label += ` — ${price}`;
                        }
                    }

                    // Figure out groups.
                    let group = null;

                    if (this.groupBy && option.custom[this.groupBy]) {
                        group = option.custom[this.groupBy];
                    }

                    let enabled = true;
                    let disabledReason = null;
                    let disabledReasonBody = null;

                    // Check for handground requirements.
                    if (option.custom['v-handground-only'] === 'yes' && !this.handgroundSelected) {
                        enabled = false;
                        disabledReason = this.$t('HANDGROUND_REQUIRED');
                        disabledReasonBody = this.$t('HANDGROUND_REQUIRED_BODY');
                    }

                    // Check for compatibility.
                    if (option.custom['v-incompatible']) {
                        const blockedBy = option.custom['v-incompatible'].split(',').map((x) => x.trim()).filter((x) => x.length > 0);

                        // eslint-disable-next-line no-restricted-syntax
                        for (const s of this.viewController.state) {
                            if (blockedBy.includes(s.component.placementIndependentCode)) {
                                enabled = false;
                                break;
                            }
                        }
                    }

                    if (atLimit) {
                        if (option.code !== this.selectedValue && !option.placeholder) {
                            enabled = false;
                        }
                    }

                    return {
                        label,
                        value: option.code,
                        selected: option.code === this.selectedValue,
                        group,
                        grouped: [],
                        groupSelected: false,
                        image: this.getImage(option),
                        color: option.custom?.hex,
                        order,
                        placeholder: option.placeholder,
                        price,
                        modalContent: hint,
                        debugValue: option.custom?.['v-parameter-value'],
                        isDisabled: !enabled,
                        disabledReason,
                        disabledReasonBody,
                        learnMore: option.custom?.['v-learn-more'],
                    };
                });

                swatches.sort((a, b) => a.order - b.order);

                if (!this.groupBy) {
                    return swatches;
                }

                // Group options.

                const groups = {};

                swatches.forEach((option) => {
                    if (groups[option.label] == null) {
                        groups[option.label] = option;
                    }

                    groups[option.label].grouped.push(option);

                    if (option.selected) {
                        groups[option.label].groupSelected = true;
                    }
                });

                return Object.values(groups);
            },

            variantWithDefault() {
                return this.variant ?? 'large';
            },

            textWidth() {
                return {
                    small: {
                        wrapperClasses: 'w-14',
                    },
                    medium: {
                        wrapperClasses: 'w-24 sm:w-28',
                    },
                    large: {
                        wrapperClasses: 'w-36 sm:w-44',
                    },
                    full: {
                        wrapperClasses: 'w-full',
                    },
                    fullContain: {
                        wrapperClasses: 'w-full',
                    },
                };
            },
        },

        methods: {
            updateGroupedSelectedValue(option, value) {
                if (this.groupBy && this.groupByDefault && option.grouped) {
                    // Pick a default value for the group.
                    const defaults = this.groupByDefault.split(',').map((x) => x.trim()).filter((x) => x.length > 0);

                    const defaultOption = option.grouped.find((o) => defaults.some((d) => d === o.group));

                    if (defaultOption) {
                        this.$emit('selectionChanged', {
                            option: defaultOption,
                            value: defaultOption.value,
                        });

                        return;
                    }
                }

                // Fallback to some (probably first) selection.
                this.$emit('selectionChanged', {
                    option,
                    value,
                });
            },

            updateSelectedValue(option, value) {
                this.$emit('selectionChanged', {
                    option,
                    value,
                });
            },

            getDescription(option) {
                const d = option.description?.[0]?.description;

                return d ?? this.viewController.componentNames(option).join(' ');
            },

            getImage(option) {
                return getImage(this.viewController, option);
            },

            getPrice(option) {
                return this.customizerStore.getOptionPrice(this.viewController, option);
            },
        },
    };
</script>
